import axios from 'axios';

// Use environment variable for API URL, with fallbacks
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  // Check for JWT auth token (new custom auth)
  const authToken = localStorage.getItem('authToken');

  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  } else {
    // Fallback to old token-based auth (if still exists)
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Add ID token for profile requests (legacy OIDC)
  const idToken = localStorage.getItem('id_token');
  if (idToken && config.url?.includes('/auth/profile')) {
    config.headers['X-Id-Token'] = idToken;
  }

  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on auth errors if it's not a login/register/2FA attempt
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
                          error.config?.url?.includes('/auth/register') ||
                          error.config?.url?.includes('/auth/custom') ||
                          error.config?.url?.includes('/auth/verify-2fa') ||
                          error.config?.url?.includes('/auth/profile'); // Don't redirect on profile errors

    const isAlreadyOnLoginPage = window.location.pathname === '/login';

    if ((error.response?.status === 401 || error.response?.status === 403) && !isAuthEndpoint && !isAlreadyOnLoginPage) {
      console.log('Auth error, redirecting to login...');
      // Clear all auth tokens
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Type definitions
export interface User {
  user_id: number;
  username: string;
  email?: string;
  role: 'user' | 'checker' | 'admin';
  account_status: string;
  discord_user_id?: string;
}

export interface UserStats {
  item_submissions: number;
  pokemon_submissions: number;
  approved_items: number;
  approved_pokemon: number;
  pending_items: number;
  pending_pokemon: number;
}

export interface RegisterData {
  username: string;
  password: string;
  email?: string;
}

export interface LoginData {
  username: string;
  password: string;
}

export interface AuthResponse {
  message: string;
  user?: User;
  token?: string;
  requires2FA?: boolean;
}

export interface RegisterResponse {
  message: string;
  user: User;
  token: string;
}

export interface ProfileResponse {
  user: User;
  stats: UserStats;
}

// Item interfaces
export interface ItemMaster {
  item_id: number;
  item_name: string;
  image_url?: string;
}

export interface ItemSale {
  id: number;
  item_id: number;
  price: number;
  date_sold: string;
  date_submitted: string;
  proof_url?: string;
  submitted_by: number;
  approved_by?: number;
  status: 'pending' | 'approved' | 'rejected';
}

export interface ItemSaleWithDetails extends ItemSale {
  item_name: string;
  image_url?: string;
  submitted_by_username: string;
  approved_by_username?: string;
}

export interface ItemSubmissionData {
  item_name: string;
  price: number;
  date_sold: string;
  proof_url?: string;
}

// Pokemon interfaces
export interface PokemonMaster {
  pokemon_id: number;
  pokemon_name: string;
}

export interface PokemonSale {
  sale_id: number;
  pokemon_id: number;
  price: number;
  date_sold: string;
  date_submitted: string;
  proof_url?: string;
  submitted_by: number;
  approved_by?: number;
  status: 'pending' | 'approved' | 'rejected';
  hp_iv?: number;
  atk_iv?: number;
  def_iv?: number;
  spatk_iv?: number;
  spdef_iv?: number;
  speed_iv?: number;
  hidden_power?: string;
  nature?: string;
  ability?: string;
  form?: string;
}

export interface PokemonSaleWithDetails extends PokemonSale {
  pokemon_name: string;
  submitted_by_username: string;
  approved_by_username?: string;
  image_url?: string;
}

export interface PokemonSubmissionData {
  pokemon_name: string;
  price: number;
  date_sold: string;
  proof_url?: string;
  level?: number;
  nature?: string;
  ability?: string;
  iv_hp?: number;
  iv_attack?: number;
  iv_defense?: number;
  iv_sp_attack?: number;
  iv_sp_defense?: number;
  iv_speed?: number;
  is_shiny?: boolean;
  held_item?: string;
}

// Price history interface
export interface PriceHistoryData {
  date: string;
  price: number;
  volume?: number;
}

export interface PriceHistoryResponse {
  item_name?: string;
  pokemon_name?: string;
  data: PriceHistoryData[];
  total_records: number;
}

// Pagination response interfaces
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export type PaginatedItemsResponse = PaginatedResponse<ItemSaleWithDetails>;
export type PaginatedPokemonResponse = PaginatedResponse<PokemonSaleWithDetails>;

// System stats interface
export interface SystemStats {
  total_users: number;
  regular_users: number;
  checkers: number;
  admins: number;
  total_item_sales: number;
  total_pokemon_sales: number;
  pending_item_reviews: number;
  pending_pokemon_reviews: number;
  approved_items: number;
  approved_pokemon: number;
}

// Authentication API calls
export const authAPI = {
  register: async (data: RegisterData): Promise<RegisterResponse> => {
    console.log('API: Sending registration request to:', `${API_BASE_URL}/auth/register`);
    console.log('API: Registration data:', data);
    const response = await api.post('/auth/register', data);
    console.log('API: Registration response:', response.data);
    return response.data;
  },

  login: async (data: LoginData, totpToken?: string): Promise<AuthResponse> => {
    // Use HTTP header for TOTP token since request body fields are being stripped
    const headers: Record<string, string> = {};
    if (totpToken) {
      headers['X-TOTP-Token'] = totpToken;
    }

    const response = await api.post('/auth/login', data, { headers });
    return response.data;
  },

  // Submit only TOTP token for 2FA verification
  submitTOTP: async (totpToken: string, pendingToken: string): Promise<AuthResponse> => {
    const headers: Record<string, string> = {
      'X-TOTP-Token': totpToken,
      'Authorization': `Pending ${pendingToken}`
    };

    const response = await api.post('/auth/verify-2fa', {}, { headers });
    return response.data;
  },

  getProfile: async (): Promise<ProfileResponse> => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  updateDiscordId: async (discordUserId: string | null): Promise<{ message: string }> => {
    const response = await api.patch('/auth/discord-id', { discord_user_id: discordUserId });
    return response.data;
  },

  getDiscordAuthUrl: async (): Promise<{ authUrl: string }> => {
    const response = await api.get('/auth/discord/auth-url');
    return response.data;
  },

  initiateDiscordOAuth: async (): Promise<void> => {
    // This will redirect to Discord OAuth with authentication
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    // Create a form and submit it to handle the redirect properly
    const form = document.createElement('form');
    form.method = 'GET';
    form.action = `${API_BASE_URL.replace('/api', '')}/api/auth/discord`;

    // Add authorization as a hidden input (though this won't work for GET)
    // Instead, we'll redirect directly with the token in a different way
    window.location.href = `${API_BASE_URL.replace('/api', '')}/api/auth/discord?token=${encodeURIComponent(token)}`;
  },

  // TOTP/MFA Management
  registerTOTP: async (): Promise<{ uri: string; secret: string }> => {
    const response = await api.post('/auth/custom/totp/register');
    return response.data;
  },

  verifyTOTPSetup: async (code: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/custom/totp/verify', { code });
    return response.data;
  },

  getTOTPStatus: async (): Promise<{ totpEnabled: boolean; totpState: string }> => {
    const response = await api.get('/auth/custom/totp/status');
    return response.data;
  },

  removeTOTP: async (): Promise<{ message: string }> => {
    const response = await api.delete('/auth/custom/totp');
    return response.data;
  },
};

// Items API calls
export const itemsAPI = {
  submit: async (data: ItemSubmissionData): Promise<{ message: string; sale_id: number }> => {
    const response = await api.post('/items/submit', data);
    return response.data;
  },

  getAll: async (filters?: {
    status?: string;
    item_name?: string;
    min_price?: number;
    max_price?: number;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedItemsResponse | ItemSaleWithDetails[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    const response = await api.get(`/items?${params.toString()}`);
    return response.data;
  },

  getById: async (saleId: number): Promise<ItemSaleWithDetails> => {
    const response = await api.get(`/items/${saleId}`);
    return response.data;
  },

  updateStatus: async (saleId: number, status: 'approved' | 'rejected', review_notes?: string): Promise<{ message: string }> => {
    const response = await api.patch(`/items/${saleId}/status`, { status, review_notes });
    return response.data;
  },

  update: async (saleId: number, data: Partial<ItemSubmissionData>): Promise<{ message: string }> => {
    const response = await api.patch(`/items/${saleId}`, data);
    return response.data;
  },

  search: async (query: string, limit?: number): Promise<ItemMaster[]> => {
    const params = new URLSearchParams({ query });
    if (limit) params.append('limit', limit.toString());
    const response = await api.get(`/items/search/autocomplete?${params.toString()}`);
    return response.data;
  },

  addMaster: async (data: { name: string; image_url?: string }): Promise<{ message: string; item_id: number }> => {
    const response = await api.post('/items/master', data);
    return response.data;
  },

  getPriceHistory: async (itemName: string, days?: number): Promise<PriceHistoryResponse> => {
    const params = new URLSearchParams();
    if (days) params.append('days', days.toString());
    const queryString = params.toString();
    const url = `/items/price-history/${encodeURIComponent(itemName)}${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },
};

// Pokemon API calls
export const pokemonAPI = {
  submit: async (data: PokemonSubmissionData): Promise<{ message: string; sale_id: number }> => {
    const response = await api.post('/pokemon/submit', data);
    return response.data;
  },

  getAll: async (filters?: {
    status?: string;
    pokemon_name?: string;
    min_price?: number;
    max_price?: number;
    date_from?: string;
    date_to?: string;
    nature?: string;
    ability?: string;
    form?: string;
    hidden_power?: string;
    min_atk_iv?: number;
    max_atk_iv?: number;
    min_def_iv?: number;
    max_def_iv?: number;
    min_speed_iv?: number;
    max_speed_iv?: number;
    min_spatk_iv?: number;
    max_spatk_iv?: number;
    min_spdef_iv?: number;
    max_spdef_iv?: number;
    min_hp_iv?: number;
    max_hp_iv?: number;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedPokemonResponse | PokemonSaleWithDetails[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    const response = await api.get(`/pokemon?${params.toString()}`);
    return response.data;
  },

  getById: async (saleId: number): Promise<PokemonSaleWithDetails> => {
    const response = await api.get(`/pokemon/${saleId}`);
    return response.data;
  },

  updateStatus: async (saleId: number, status: 'approved' | 'rejected'): Promise<{ message: string }> => {
    const response = await api.patch(`/pokemon/${saleId}/status`, { status });
    return response.data;
  },

  update: async (saleId: number, data: Partial<PokemonSubmissionData>): Promise<{ message: string }> => {
    const response = await api.patch(`/pokemon/${saleId}`, data);
    return response.data;
  },

  search: async (query: string, limit?: number): Promise<PokemonMaster[]> => {
    const params = new URLSearchParams({ query });
    if (limit) params.append('limit', limit.toString());
    const response = await api.get(`/pokemon/search/autocomplete?${params.toString()}`);
    return response.data;
  },

  addMaster: async (data: { name: string; image_url?: string }): Promise<{ message: string; pokemon_id: number }> => {
    const response = await api.post('/pokemon/master', data);
    return response.data;
  },

  getPriceHistory: async (pokemonName: string, days?: number): Promise<PriceHistoryResponse> => {
    const params = new URLSearchParams();
    if (days) params.append('days', days.toString());
    const queryString = params.toString();
    const url = `/pokemon/price-history/${encodeURIComponent(pokemonName)}${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },
};

// Abilities API calls
export const abilitiesAPI = {
  getAll: async (): Promise<{ ability_id: number; ability_name: string }[]> => {
    const response = await api.get('/abilities');
    return response.data;
  },

  getById: async (id: number): Promise<{ ability_id: number; ability_name: string }> => {
    const response = await api.get(`/abilities/${id}`);
    return response.data;
  },
};

// Admin API calls
export const adminAPI = {
  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  updateUserRole: async (userId: number, role: 'user' | 'checker' | 'admin'): Promise<{ message: string }> => {
    const response = await api.patch(`/admin/users/${userId}/role`, { role });
    return response.data;
  },

  updateUserStatus: async (userId: number, status: 'Active' | 'Suspended'): Promise<{ message: string }> => {
    const response = await api.patch(`/admin/users/${userId}/status`, { account_status: status });
    return response.data;
  },

  getSystemStats: async (): Promise<SystemStats> => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  getUserStats: async (userId: number): Promise<UserStats> => {
    const response = await api.get(`/admin/users/${userId}/stats`);
    return response.data;
  },

  getSystemSettings: async (): Promise<Record<string, string>> => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  updateSystemSetting: async (settingKey: string, value: string): Promise<{ message: string }> => {
    const response = await api.patch(`/admin/settings/${settingKey}`, { value });
    return response.data;
  },

  detectDuplicates: async (): Promise<any> => {
    const response = await api.get('/admin/duplicates');
    return response.data;
  },

  deleteDuplicateEntry: async (type: 'item' | 'pokemon', id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/duplicates/${type}/${id}`);
    return response.data;
  },

  // Data Management APIs
  getAllPokemon: async (params?: any): Promise<any> => {
    const response = await api.get('/admin/pokemon', { params });
    return response.data;
  },

  updatePokemon: async (saleId: number, data: any): Promise<{ message: string }> => {
    const response = await api.patch(`/admin/pokemon/${saleId}`, data);
    return response.data;
  },

  getAllItems: async (params?: any): Promise<any> => {
    const response = await api.get('/admin/items', { params });
    return response.data;
  },

  updateItem: async (saleId: number, data: any): Promise<{ message: string }> => {
    const response = await api.patch(`/admin/items/${saleId}`, data);
    return response.data;
  },

  getAllSprites: async (params?: any): Promise<any> => {
    const response = await api.get('/admin/sprites', { params });
    return response.data;
  },

  createSprite: async (data: any): Promise<{ message: string; id: number }> => {
    const response = await api.post('/admin/sprites', data);
    return response.data;
  },

  updateSprite: async (id: number, data: any): Promise<{ message: string }> => {
    const response = await api.patch(`/admin/sprites/${id}`, data);
    return response.data;
  },

  deleteSprite: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/sprites/${id}`);
    return response.data;
  },
};

// Logging API types
export interface LogFile {
  name: string;
  path: string;
  type: 'general' | 'security' | 'database' | 'api' | 'errors';
  size: number;
  lastModified: string;
}

export interface EnhancedLogEntry {
  id: string;
  timestamp: string;
  level: 'error' | 'warn' | 'info' | 'success' | 'security' | 'database';
  category: 'database' | 'security' | 'api' | 'errors' | 'discord' | 'general';
  message: string;
  metadata?: any;
  user?: string;
  ip?: string;
}

export interface LogFilter {
  limit?: number;
  level?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  user?: string;
}

// Logging API calls
export const loggingAPI = {
  getLogFiles: async (): Promise<LogFile[]> => {
    const response = await api.get('/logs/files');
    return response.data;
  },

  getLogContent: async (fileName: string, lines: number = 100): Promise<string[]> => {
    const response = await api.get(`/logs/content/${fileName}?lines=${lines}`);
    return response.data.content;
  },

  searchLogs: async (fileName: string, query: string, limit: number = 50): Promise<string[]> => {
    const response = await api.get(`/logs/search/${fileName}?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data.results;
  },

  // NEW: Get categorized logs with enhanced filtering
  getCategoryLogs: async (category: string, filters: LogFilter = {}): Promise<{ logs: EnhancedLogEntry[]; category: string }> => {
    const params = new URLSearchParams();

    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.level && filters.level !== 'all') params.append('level', filters.level);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.search) params.append('search', filters.search);
    if (filters.user) params.append('user', filters.user);

    const queryString = params.toString();
    const url = `/logs/category/${category}${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response.data;
  },
};

// Image Recognition API types
export interface RecognizedPokemonStats {
  name?: string;
  level?: number;
  nature?: string;
  ability?: string;
  hp?: number;
  attack?: number;
  defense?: number;
  spAttack?: number;
  spDefense?: number;
  speed?: number;
}

export interface ImageRecognitionResponse {
  success: boolean;
  stats: RecognizedPokemonStats;
  confidence: number;
  message: string;
  rawText?: string;
}

// Image Recognition API calls
export const imageAPI = {
  recognizeStats: async (imageFile: File): Promise<ImageRecognitionResponse> => {
    const formData = new FormData();
    formData.append('pokemonImage', imageFile);

    const response = await api.post('/image/recognize', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getGuidelines: async () => {
    const response = await api.get('/image/guidelines');
    return response.data;
  },

  testService: async () => {
    const response = await api.get('/image/test');
    return response.data;
  },
};

// 2FA types
export interface TwoFASetupResponse {
  secret: string;
  qrCode: string;
  manualEntryKey: string;
  issuer: string;
  accountName: string;
}

export interface TwoFAStatusResponse {
  is2FAEnabled: boolean;
  hasSecret: boolean;
}

// 2FA API calls
export const twoFAAPI = {
  getStatus: async (): Promise<TwoFAStatusResponse> => {
    const response = await api.get('/2fa/status');
    return response.data;
  },

  setup: async (): Promise<TwoFASetupResponse> => {
    const response = await api.post('/2fa/setup');
    return response.data;
  },

  verify: async (token: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/2fa/verify', { token });
    return response.data;
  },

  disable: async (token: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/2fa/disable', { token });
    return response.data;
  },
};

export default api;
